// components/PendingApprovalsTable.tsx
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { approveRequest, rejectRequest } from 'store/approvalsSlice';
import { ColumnDef } from '@tanstack/react-table';
import PhoenixLoader from 'components/common/PhoenixLoader';
import FeatherIcon from 'feather-icons-react';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { AppDispatch, RootState } from 'store';
import { Badge } from 'react-bootstrap';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import ViewPendingApprovalModal from 'components/modals/ViewPendingApprovalModal';
import { Dropdown } from 'react-bootstrap';

// Add these imports:
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faCheck,
  faXmark,
  faBuilding,
  faSitemap,
  faUser,
  faTag,
  faCalendar,
  faExclamationCircle,
  faCog
} from '@fortawesome/free-solid-svg-icons';

const statusColors: Record<string, string> = {
  approved: 'success',
  pending: 'warning',
  rejected: 'danger'
};

interface DepartmentRequest {
  id: number;
  asset_id: number;
  category_id: number;
  request_note: string;
  priority: string;
  user_id: number;
  company_id: number;
  status: string;
  created_at: string;
  updated_at: string;
  user_name: string;
  asset_name: string;
  category_name: string;
  company_name: string;
  approver_name: string;
  departmentName: string;
  requestId: number;
  createdAt: string;
}

interface PendingApprovalsTableProps {
  requests: DepartmentRequest[];
  searchQuery: string;
}

const PendingApprovalsTable: React.FC<PendingApprovalsTableProps> = ({
  requests,
  searchQuery
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedRequest, setSelectedRequest] =
    useState<DepartmentRequest | null>(null);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return requests;

    return requests.filter(
      dr =>
        dr.departmentName.toLowerCase().includes(q) ||
        dr.user_name?.toLowerCase().includes(q) ||
        dr.asset_name?.toLowerCase().includes(q) ||
        dr.category_name?.toLowerCase().includes(q) ||
        dr.status.toLowerCase().includes(q) ||
        dr.company_name?.toLowerCase().includes(q)
    );
  }, [requests, searchQuery]);

  const handleApprove = async (id: number) => {
    try {
      setLoadingId(id);
      // Approve the request (backend handles notification)
      await dispatch(approveRequest(id)).unwrap();
    } catch (error) {
      console.error('Failed to approve request:', error);
    } finally {
      setLoadingId(null);
    }
  };

  const handleReject = async (id: number) => {
    try {
      setLoadingId(id);
      // Reject the request (backend handles notification)
      await dispatch(rejectRequest(id)).unwrap();
    } catch (error) {
      console.error('Failed to reject request:', error);
    } finally {
      setLoadingId(null);
    }
  };

  // ADDED: Handle view request
  const handleViewRequest = (request: DepartmentRequest) => {
    setSelectedRequest(request);
    setShowViewModal(true);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { variant: 'warning', label: 'Pending' },
      approved: { variant: 'success', label: 'Approved' },
      rejected: { variant: 'danger', label: 'Rejected' },
      cancelled: { variant: 'secondary', label: 'Cancelled' },
      completed: { variant: 'info', label: 'Completed' },
      in_progress: { variant: 'primary', label: 'In Progress' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      variant: 'secondary',
      label: status
    };
    return <Badge bg={config.variant}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      low: { variant: 'success', label: 'Low' },
      medium: { variant: 'warning', label: 'Medium' },
      high: { variant: 'danger', label: 'High' }
    };

    const config = priorityConfig[priority as keyof typeof priorityConfig] || {
      variant: 'secondary',
      label: priority
    };
    return <Badge bg={config.variant}>{config.label}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const columns = useMemo<ColumnDef<DepartmentRequest>[]>(
    () => [
      {
        id: 'sn',
        header: 'SN',
        cell: ({ row }) => <span className="fw-semibold">{row.index + 1}</span>,
        meta: {
          headerProps: {
            style: { width: '80px' }
          },
          cellProps: {}
        }
      },
      {
        accessorKey: 'company_name',
        header: () => (
          <span>
            <FontAwesomeIcon icon={faBuilding} className="me-2 text-primary" />
            Company
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-body">{(getValue() as string) || 'N/A'}</span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        accessorKey: 'departmentName',
        header: () => (
          <span>
            <FontAwesomeIcon icon={faSitemap} className="me-2 text-primary" />
            Department
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-body">{getValue() as string}</span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        accessorKey: 'user_name',
        header: () => (
          <span>
            <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
            Requestor
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-body">{getValue() as string}</span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        accessorKey: 'category_name',
        header: () => (
          <span>
            <FontAwesomeIcon icon={faTag} className="me-2 text-primary" />
            Category
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-body">{(getValue() as string) || 'N/A'}</span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      // {
      //   accessorKey: 'priority',
      //   header: 'Priority',
      //   cell: ({ getValue }) => getPriorityBadge(getValue() as string),
      //   meta: {
      //     headerProps: {
      //       className: 'ps-2 bg-white py-3 text-uppercase'
      //     },
      //     cellProps: { className: 'ps-2' }
      //   }
      // },
      // {
      //   accessorKey: 'status',
      //   header: 'Status',
      //   cell: ({ getValue }) => getStatusBadge(getValue() as string),
      //   meta: {
      //     headerProps: {
      //       className: 'ps-2 bg-white py-3 text-uppercase'
      //     },
      //     cellProps: { className: 'ps-2' }
      //   }
      // },
      {
        accessorKey: 'priority',
        header: () => (
          <span>
            <FontAwesomeIcon
              icon={faExclamationCircle}
              className="me-2 text-primary"
            />
            Priority
          </span>
        ),
        cell: ({ getValue }) => {
          const priority = (getValue() as string)?.toLowerCase();

          const config: Record<
            string,
            { className: string; icon: string; label: string }
          > = {
            high: {
              className: 'badge-phoenix-danger',
              icon: 'arrow-up',
              label: 'High'
            },
            medium: {
              className: 'badge-phoenix-warning',
              icon: 'minus',
              label: 'Medium'
            },
            low: {
              className: 'badge-phoenix-success',
              icon: 'arrow-down',
              label: 'Low'
            }
          };

          const badge = config[priority] || {
            className: 'badge-phoenix-secondary',
            icon: 'help-circle',
            label: priority
          };

          return (
            <span className={`badge badge-phoenix fs-10 ${badge.className}`}>
              <span className="badge-label">{badge.label}</span>
              <FeatherIcon icon={badge.icon} size={12} className="ms-1" />
            </span>
          );
        },
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue }) => {
          const status = (getValue() as string)?.toLowerCase();

          // Map status to Phoenix badge color and professional icon
          const statusConfig: Record<
            string,
            { className: string; icon: string; label: string }
          > = {
            fulfilled: {
              className: 'badge-phoenix-success', // green
              icon: 'check-circle', // professional “completed” icon
              label: 'Fulfilled'
            },
            'on-hold': {
              className: 'badge-phoenix-warning', // yellow
              icon: 'pause-circle', // professional “on-hold” icon
              label: 'On Hold'
            },
            approved: {
              className: 'badge-phoenix-primary', // blue
              icon: 'thumbs-up', // professional “approved” icon
              label: 'Approved'
            },
            rejected: {
              className: 'badge-phoenix-danger', // red
              icon: 'x-circle', // professional “rejected” icon
              label: 'Rejected'
            },
            pending: {
              className: 'badge-phoenix-info', // cyan/neutral
              icon: 'clock', // professional “pending” icon
              label: 'Pending'
            }
          };

          const badge = statusConfig[status] || {
            className: 'badge-phoenix-secondary',
            icon: 'help-circle',
            label: status
          };

          return (
            <span className={`badge badge-phoenix fs-10 ${badge.className}`}>
              <span className="badge-label">{badge.label}</span>
              <FeatherIcon icon={badge.icon} size={12} className="ms-1" />
            </span>
          );
        },
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        accessorKey: 'created_at',
        header: () => (
          <span>
            <FontAwesomeIcon icon={faCalendar} className="me-2 text-primary" />
            Date
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-body">{formatDate(getValue() as string)}</span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        id: 'actions',
        header: () => (
          <div className="d-inline-flex align-items-center">
            <FontAwesomeIcon icon={faCog} className="me-2 text-primary" />
            Actions
          </div>
        ),
        cell: ({ row: { original } }) => (
          <>
            {loadingId === original.id ? (
              <PhoenixLoader />
            ) : (
              <RevealDropdownTrigger>
                <RevealDropdown>
                  <div>
                    <button
                      className="dropdown-item"
                      onClick={() => handleViewRequest(original)}
                    >
                      <FontAwesomeIcon
                        icon={faEye}
                        className="me-2 text-info"
                      />
                      View
                    </button>
                    <button
                      className="dropdown-item text-success"
                      onClick={() => handleApprove(original.id)}
                    >
                      <FontAwesomeIcon icon={faCheck} className="me-2" />
                      Approve
                    </button>
                    <Dropdown.Divider />
                    <button
                      className="dropdown-item text-danger"
                      onClick={() => handleReject(original.id)}
                    >
                      <FontAwesomeIcon icon={faXmark} className="me-2" />
                      Reject
                    </button>
                  </div>
                </RevealDropdown>
              </RevealDropdownTrigger>
            )}
          </>
        ),
        meta: {
          headerProps: {
            className: 'text-end pe-3'
          },
          cellProps: { className: 'text-end pe-3' }
        }
      }
    ],
    [loadingId]
  );

  const table = useAdvanceTable({
    data: filtered,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <>
      <AdvanceTableProvider {...table}>
        <div
          className="bg-white"
          style={{
            borderRadius: '12px',
            border: '1px solid #e5e7eb'
          }}
        >
          <div className="table-responsive" style={{ overflowY: 'visible' }}>
            <AdvanceTable
              tableProps={{
                className: 'phoenix-table mb-0',
                hover: true
              }}
              headerClassName=""
              rowClassName="hover-actions-trigger btn-reveal-trigger align-middle"
            />
          </div>
          <div className="table-footer-elegant">
            <AdvanceTableFooter pagination className="px-3 py-2" />
          </div>
        </div>
      </AdvanceTableProvider>

      {/* ADDED: View Modal */}
      <ViewPendingApprovalModal
        show={showViewModal}
        handleClose={() => {
          setShowViewModal(false);
          setSelectedRequest(null);
        }}
        request={selectedRequest}
      />
    </>
  );
};

export default PendingApprovalsTable;
